import React, {useEffect, useState} from 'react'
import {X, Bell, Info, CheckCircle, AlertTriangle, AlertCircle} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {useToast, Toast} from '../hooks/useToast'
import {useThemeMode, systemMode} from '../../_payairo/partials'

const ToastItem: React.FC<{toast: Toast}> = ({toast}) => {
  const {removeToast} = useToast()
  const navigate = useNavigate()
  const {mode} = useThemeMode()
  const effectiveMode = mode === 'system' ? systemMode : mode
  const [progress, setProgress] = useState(100)
  const duration = toast.duration || 5000

  useEffect(() => {
    const startTime = Date.now()
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100)
      setProgress(remaining)
      if (remaining === 0) {
        clearInterval(timer)
        removeToast(toast.id)
      }
    }, 50)

    return () => clearInterval(timer)
  }, [toast.id, duration, removeToast])

  const getIcon = () => {
    const size = 20
    switch (toast.type) {
      case 'success':
        return <CheckCircle size={size} color='#10b981' />
      case 'warning':
        return <AlertTriangle size={size} color='#f59e0b' />
      case 'error':
        return <AlertCircle size={size} color='#ef4444' />
      default:
        return <Bell size={size} color='#3b82f6' />
    }
  }

  return (
    <div
      className='toast-item'
      style={{
        width: '320px',
        backgroundColor: effectiveMode === 'dark' ? '#1e293b' : '#fff',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        borderLeft: '4px solid #3b82f6',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        animation: 'toast-slide-in 0.3s ease forwards',
        marginBottom: '10px',
      }}
      onClick={() => navigate('/notifications')}
    >
      <div className='p-4 d-flex gap-3 align-items-start'>
        <div className='flex-shrink-0 mt-1'>{getIcon()}</div>
        <div className='flex-grow-1 min-w-0'>
          <h4
            className='fs-6 fw-bold mb-1'
            style={{color: effectiveMode === 'dark' ? '#fff' : '#111827'}}
          >
            {toast.title}
          </h4>
          <p className='fs-7 text-muted mb-1 text-truncate'>{toast.message}</p>
          <span className='fs-8 text-muted opacity-75'>Just now</span>
        </div>
        <button
          className='btn btn-icon btn-sm btn-active-light-primary border-0 p-0'
          style={{backgroundColor: 'transparent'}}
          onClick={(e) => {
            e.stopPropagation()
            removeToast(toast.id)
          }}
        >
          <X size={16} color='#94a3b8' />
        </button>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: '3px',
          width: `${progress}%`,
          backgroundColor: '#3b82f6',
          transition: 'width 0.05s linear',
        }}
      />
    </div>
  )
}

export const ToastContainer: React.FC = () => {
  const {toasts} = useToast()

  useEffect(() => {
    if (!document.getElementById('toast-animations')) {
      const style = document.createElement('style')
      style.id = 'toast-animations'
      style.innerHTML = `
        @keyframes toast-slide-in {
          from { transform: translateX(110%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes toast-fade-out {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(110%); opacity: 0; }
        }
        .toast-container {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9999;
          display: flex;
          flex-direction: column-reverse; /* Stack toasts upwards from bottom-right */
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  return (
    <div className='toast-container'>
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
